// =============================================================================
// Cash in shops — current position and movement trail for HQ
// =============================================================================
// This is intentionally not a daily close report. Owner, admin, finance and
// operations need four answers:
//   1. How much cash is in each shop right now?
//   2. What did staff physically count most recently?
//   3. Did that count match the system at the time?
//   4. Where did the money come from, and where did it go?

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/EmptyState';
import { PageScaffold } from '../../components/PageScaffold';
import { StatusPill } from '../../components/StatusPill';
import { cn } from '../../lib/cn';

interface LatestCount {
  day: string;
  expectedCash: string;
  actualCash: string;
  discrepancy: string;
  expectedCheques: string;
  actualCheques: string;
  chequeDiscrepancy: string;
  reason: string | null;
  closedByName: string | null;
  closedAt: string;
}

interface ShopFlow {
  shopId: string;
  shopCode: string;
  shopName: string;
  cashAccountCode: string | null;
  balance: string;
  chequeBalance: string;
  opening: string;
  openingLabel: string;
  moneyIn: string;
  moneyOut: string;
  bySource: Array<{ source: string; label: string; count: number; in: string; out: string }>;
  latestCount: LatestCount | null;
}

interface Overview {
  shops: ShopFlow[];
  totals: { inDrawers: string; unexplainedCount: number; unexplainedAmount: string };
}

interface Movement {
  id: string;
  at: string;
  shopId: string;
  shopCode: string;
  source: string;
  label: string;
  amount: string;
  orderId: string | null;
  orderNumber: string | null;
  customerName: string | null;
  byName: string | null;
}

type Direction = 'all' | 'in' | 'out';

function fmtDay(day: string): string {
  return new Date(day).toLocaleDateString('en-MU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function differenceText(value: number): string {
  if (value === 0) return 'Matched';
  return value > 0 ? 'Over' : 'Short';
}

export function CashFlowPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const shopId = searchParams.get('shopId') ?? '';
  const [direction, setDirection] = useState<Direction>('all');
  const selectShop = (nextShopId: string): void => {
    const next = new URLSearchParams(searchParams);
    if (nextShopId) next.set('shopId', nextShopId);
    else next.delete('shopId');
    setSearchParams(next, { replace: true });
  };

  // Always load the all-shop overview. Filtering it in the browser keeps the
  // shop picker stable and makes returning to "All shops" immediate.
  const overview = useQuery({
    queryKey: ['cash-flow-overview'],
    queryFn: () => apiGet<Overview>('/cash-flow/overview'),
  });

  const movements = useQuery({
    queryKey: ['cash-flow-movements', shopId, direction],
    queryFn: () =>
      apiGet<{ movements: Movement[]; truncated: boolean }>('/cash-flow/movements', {
        ...(shopId ? { shopId } : {}),
        ...(direction !== 'all' ? { direction } : {}),
        limit: '150',
      }),
  });

  const allShops = overview.data?.shops ?? [];
  const shownShops = shopId ? allShops.filter((shop) => shop.shopId === shopId) : allShops;
  const shownCash = shownShops.reduce((sum, shop) => sum + Number(shop.balance), 0);
  const shownCheques = shownShops.reduce((sum, shop) => sum + Number(shop.chequeBalance), 0);
  const shopsWithDifference = shownShops.filter((shop) => {
    const count = shop.latestCount;
    return count && (Number(count.discrepancy) !== 0 || Number(count.chequeDiscrepancy) !== 0);
  });
  const absoluteDifference = shopsWithDifference.reduce((sum, shop) => {
    const count = shop.latestCount!;
    return sum + Math.abs(Number(count.discrepancy)) + Math.abs(Number(count.chequeDiscrepancy));
  }, 0);

  return (
    <PageScaffold
      title="Cash in shops"
      subtitle="Current balances, the latest physical counts, and every movement in or out."
      actions={
        allShops.length > 1 ? (
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-ink-3">Shop</span>
            <select
              value={shopId}
              onChange={(e) => selectShop(e.target.value)}
              className="bg-surface border border-rule rounded px-3 py-2 text-sm text-ink min-w-[220px] focus:outline-none focus:border-accent"
            >
              <option value="">All shops</option>
              {allShops.map((shop) => (
                <option key={shop.shopId} value={shop.shopId}>
                  {shop.shopCode} — {shop.shopName}
                </option>
              ))}
            </select>
          </label>
        ) : undefined
      }
    >
      {overview.isLoading ? (
        <div className="text-center text-ink-3 py-12">Loading cash position…</div>
      ) : allShops.length === 0 ? (
        <Card>
          <EmptyState
            title="No shops yet"
            description="Add a shop and connect its cash account to see its cash position."
          />
        </Card>
      ) : (
        <>
          <Card bodyClassName="p-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-rule">
              <Summary
                label={shopId ? 'Cash in this shop now' : 'Cash in all shops now'}
                value={<Money value={shownCash} className="text-current" />}
                tone="text-ink"
              />
              <Summary
                label="Cheques held"
                value={<Money value={shownCheques} className="text-current" />}
                tone="text-ink"
              />
              <Summary
                label="Latest counts with a difference"
                value={
                  shopsWithDifference.length === 0 ? (
                    <span>None</span>
                  ) : (
                    <span>
                      {shopsWithDifference.length}
                      <span className="text-sm font-normal ml-2">
                        · <Money value={absoluteDifference} className="text-current" />
                      </span>
                    </span>
                  )
                }
                tone={shopsWithDifference.length === 0 ? 'text-positive' : 'text-negative'}
              />
            </div>
          </Card>

          <Card
            title={shopId ? 'Cash position' : 'Cash in each shop'}
            subtitle="Live book balance compared with the most recent physical count"
            bodyClassName="p-4"
          >
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {shownShops.map((shop) => (
                <ShopPositionCard
                  key={shop.shopId}
                  shop={shop}
                  onSelect={() => selectShop(shop.shopId)}
                  selectable={!shopId}
                />
              ))}
            </div>
          </Card>

          <Card
            title="Where money came from and where it went"
            subtitle="Movements behind each current shop balance, grouped by source"
            bodyClassName="p-0"
          >
            {shownShops.every((shop) => shop.bySource.length === 0) ? (
              <EmptyState
                title="No movement yet"
                description="No cash has moved through the selected shop accounts."
              />
            ) : (
              <div className="divide-y divide-rule-2">
                {shownShops
                  .filter((shop) => shop.bySource.length > 0)
                  .map((shop) => (
                    <div key={shop.shopId} className="px-6 py-4">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <div className="flex items-baseline gap-2">
                          <StatusPill>{shop.shopCode}</StatusPill>
                          <span className="text-xs text-ink-3">{shop.shopName}</span>
                        </div>
                        <span className="text-[11px] text-ink-3">
                          {shop.openingLabel}:{' '}
                          <Money value={shop.opening} className="text-current" />
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {shop.bySource.map((source) => (
                          <div
                            key={source.source}
                            className="flex items-center justify-between gap-4 text-sm"
                          >
                            <span className="text-ink-3">
                              {source.label}
                              <span className="text-ink-3/60 text-xs ml-2">×{source.count}</span>
                            </span>
                            <span className="tabular-nums whitespace-nowrap">
                              {Number(source.in) > 0 && (
                                <span className="text-positive mr-4">
                                  +<Money value={source.in} className="text-current" />
                                </span>
                              )}
                              {Number(source.out) > 0 && (
                                <span className="text-negative">
                                  −<Money value={source.out} className="text-current" />
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Card>

          <Card
            title="Every cash movement"
            subtitle="The order or action, the person who recorded it, and the amount"
            bodyClassName="p-0"
            action={
              <div className="flex gap-1">
                {(['all', 'in', 'out'] as Direction[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setDirection(value)}
                    className={cn(
                      'text-xs px-2.5 py-1 rounded border transition',
                      direction === value
                        ? 'border-accent bg-accent-soft text-ink'
                        : 'border-rule text-ink-3 hover:border-rule-2',
                    )}
                  >
                    {value === 'all' ? 'All' : value === 'in' ? 'Money in' : 'Money out'}
                  </button>
                ))}
              </div>
            }
          >
            {movements.isLoading ? (
              <div className="text-center text-ink-3 px-6 py-8">Loading movements…</div>
            ) : (movements.data?.movements ?? []).length === 0 ? (
              <EmptyState title="No movements" description="Nothing matches this filter." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                        <th className="px-6 py-2 font-medium">Date</th>
                        <th className="px-6 py-2 font-medium">Shop</th>
                        <th className="px-6 py-2 font-medium">What</th>
                        <th className="px-6 py-2 font-medium">Customer</th>
                        <th className="px-6 py-2 font-medium">By</th>
                        <th className="px-6 py-2 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(movements.data?.movements ?? []).map((movement) => {
                        const amount = Number(movement.amount);
                        return (
                          <tr key={movement.id} className="border-b border-rule-2">
                            <td className="px-6 py-3 whitespace-nowrap">{fmtDay(movement.at)}</td>
                            <td className="px-6 py-3">
                              <StatusPill>{movement.shopCode}</StatusPill>
                            </td>
                            <td className="px-6 py-3">
                              {movement.orderId ? (
                                <Link
                                  to={`/orders/${movement.orderId}`}
                                  className="text-accent hover:underline"
                                >
                                  {movement.label}
                                </Link>
                              ) : (
                                <span className="text-ink">{movement.label}</span>
                              )}
                            </td>
                            <td className="px-6 py-3 text-xs text-ink-3">
                              {movement.customerName ?? '—'}
                            </td>
                            <td className="px-6 py-3 text-xs text-ink-3">
                              {movement.byName ?? '—'}
                            </td>
                            <td className="px-6 py-3 text-right tabular-nums">
                              <span className={amount >= 0 ? 'text-positive' : 'text-negative'}>
                                {amount >= 0 ? '+' : '−'}
                                <Money value={Math.abs(amount)} className="text-current" />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {movements.data?.truncated && (
                  <p className="px-6 py-3 text-xs text-ink-3 border-t border-rule">
                    Showing the most recent 150 movements. Select one shop to narrow the trail.
                  </p>
                )}
              </>
            )}
          </Card>
        </>
      )}
    </PageScaffold>
  );
}

function ShopPositionCard({
  shop,
  onSelect,
  selectable,
}: {
  shop: ShopFlow;
  onSelect: () => void;
  selectable: boolean;
}): JSX.Element {
  const count = shop.latestCount;
  const cashDifference = Number(count?.discrepancy ?? 0);
  const chequeDifference = Number(count?.chequeDiscrepancy ?? 0);
  const hasDifference = !!count && (cashDifference !== 0 || chequeDifference !== 0);
  const hasChequeCount =
    !!count &&
    (Number(count.expectedCheques) !== 0 ||
      Number(count.actualCheques) !== 0 ||
      chequeDifference !== 0);

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!selectable}
      className={cn(
        'rounded border border-rule bg-surface text-left overflow-hidden',
        selectable && 'hover:border-accent/60 transition cursor-pointer',
      )}
    >
      <div className="px-4 py-3 border-b border-rule bg-paper-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-medium text-ink">{shop.shopCode}</span>
            <span className="text-xs text-ink-3 truncate">{shop.shopName}</span>
          </div>
        </div>
        {!count ? (
          <span className="text-[10px] uppercase tracking-wider text-ink-3">No count yet</span>
        ) : hasDifference ? (
          <span className="text-[10px] uppercase tracking-wider text-negative">Difference</span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-positive">Matched</span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">
              Cash in shop now
            </div>
            <Money value={shop.balance} className="text-2xl font-semibold text-ink" />
          </div>
          {Number(shop.chequeBalance) !== 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Cheques held</div>
              <Money value={shop.chequeBalance} className="text-sm text-ink" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4 py-3 border-y border-rule-2">
          <CompactAmount label={shop.openingLabel} value={shop.opening} />
          <CompactAmount label="Money in" value={shop.moneyIn} tone="text-positive" prefix="+" />
          <CompactAmount label="Money out" value={shop.moneyOut} tone="text-negative" prefix="−" />
        </div>

        {count ? (
          <div className="mt-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-xs font-medium text-ink">Latest physical count</span>
              <span className="text-[11px] text-ink-3">
                {fmtDay(count.day)}
                {count.closedByName ? ` · ${count.closedByName}` : ''}
              </span>
            </div>
            <dl className="mt-2 space-y-1.5 text-sm">
              <AmountRow label="System expected at count" value={count.expectedCash} />
              <AmountRow label="Staff counted" value={count.actualCash} />
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-3">Cash difference</dt>
                <dd
                  className={cn(
                    'font-medium tabular-nums',
                    cashDifference === 0
                      ? 'text-positive'
                      : cashDifference > 0
                        ? 'text-warning'
                        : 'text-negative',
                  )}
                >
                  {cashDifference !== 0 && `${differenceText(cashDifference)} `}
                  {cashDifference > 0 ? '+' : ''}
                  <Money value={cashDifference} className="text-current" />
                </dd>
              </div>
              {hasChequeCount && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-ink-3">Cheque difference</dt>
                  <dd
                    className={cn(
                      'font-medium tabular-nums',
                      chequeDifference === 0
                        ? 'text-positive'
                        : chequeDifference > 0
                          ? 'text-warning'
                          : 'text-negative',
                    )}
                  >
                    {chequeDifference !== 0 && `${differenceText(chequeDifference)} `}
                    {chequeDifference > 0 ? '+' : ''}
                    <Money value={chequeDifference} className="text-current" />
                  </dd>
                </div>
              )}
            </dl>
            {hasDifference && count.reason && (
              <div className="mt-3 rounded bg-warning-soft px-3 py-2 text-xs text-ink-2">
                <span className="font-medium">Reason:</span> {count.reason}
              </div>
            )}
          </div>
        ) : (
          <p className="mt-4 text-xs text-ink-3">
            Staff have not submitted a physical drawer count yet. The live balance above still comes
            from recorded cash movements.
          </p>
        )}
      </div>
    </button>
  );
}

function Summary({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone: string;
}): JSX.Element {
  return (
    <div className="px-6 py-5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">{label}</div>
      <div className={cn('mt-1 text-2xl font-semibold', tone)}>{value}</div>
    </div>
  );
}

function CompactAmount({
  label,
  value,
  prefix,
  tone = 'text-ink',
}: {
  label: string;
  value: string;
  prefix?: string;
  tone?: string;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-[10px] text-ink-3 truncate">{label}</div>
      <div className={cn('text-xs tabular-nums truncate', tone)}>
        {prefix}
        <Money value={value} className="text-current" />
      </div>
    </div>
  );
}

function AmountRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-3">{label}</dt>
      <dd className="text-ink tabular-nums">
        <Money value={value} />
      </dd>
    </div>
  );
}
